"use client";

import type React from "react";

import { useState, useMemo } from "react";
import { SearchInput } from "./search-input";
import { FAQList } from "./faq-list";
import { SanityDocument } from "@sanity/client";
import { ResultsCounter } from "./results-counter";

interface SearchableFAQAccordionProps {
  faqs: SanityDocument[];
}

export default function SearchableFAQAccordion({
  faqs,
}: Readonly<SearchableFAQAccordionProps>) {
  const [searchQuery, setSearchQuery] = useState("");
  const [displayCount, setDisplayCount] = useState(7);

  // Filter FAQs based on search query
  const filteredFAQs = useMemo(() => {
    if (!searchQuery.trim()) return faqs;

    const lowerCaseQuery = searchQuery.toLowerCase();
    return faqs.filter(
      (faq) =>
        faq.question.toLowerCase().includes(lowerCaseQuery) ??
        faq.answer.toLowerCase().includes(lowerCaseQuery),
    );
  }, [searchQuery, faqs]);

  function sanitizeInput(input: string): string {
    return input.replace(/[<>]/g, ""); // remove < and >
  }

  // Reset display count when search query changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedQuery = sanitizeInput(e.target.value);
    setSearchQuery(sanitizedQuery);
    setDisplayCount(7); // Reset to initial display count when search changes
  };

  // Get FAQs to display based on current display count
  const displayedFAQs = filteredFAQs.slice(0, displayCount);

  return (
    <div className="mx-auto w-full max-w-3xl py-8 md:px-4">
      <SearchInput
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
      />

      <FAQList faqs={displayedFAQs} />
      <ResultsCounter
        displayedCount={displayedFAQs.length}
        totalCount={filteredFAQs.length}
        searchQuery={searchQuery}
      />
    </div>
  );
}
